import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2, XCircle, BookOpen, Loader2 } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { SeverityBadge, StatusBadge, confidenceLabel } from "../components/SeverityBadge";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

function highlight(text, quote) {
  if (!quote) return [text];
  const map = [];
  let norm = "";
  for (let i = 0; i < text.length; i++) {
    if (/\s/.test(text[i])) {
      if (norm === "" || norm.endsWith(" ")) continue;
      norm += " ";
      map.push(i);
    } else {
      norm += text[i].toLowerCase();
      map.push(i);
    }
  }
  const nq = quote.toLowerCase().replace(/\s+/g, " ").trim();
  let idx = -1;
  for (const len of [80, 40, 25]) {
    idx = norm.indexOf(nq.slice(0, len));
    if (idx !== -1) break;
  }
  if (idx === -1) return [text];
  const start = map[idx];
  const endIdx = Math.min(idx + nq.length, map.length) - 1;
  const end = map[endIdx] + 1;
  return [text.slice(0, start), <mark key="m" className="svp-highlight">{text.slice(start, end)}</mark>, text.slice(end)];
}

export default function FindingSplitView() {
  const { auditId, findingId } = useParams();
  const navigate = useNavigate();
  const [finding, setFinding] = useState(null);
  const [audit, setAudit] = useState(null);
  const [page, setPage] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [f, a] = await Promise.all([api.get(`/findings/${findingId}`), api.get(`/audits/${auditId}`)]);
        setFinding(f.data);
        setAudit(a.data);
        setPageNum(f.data.evidence?.[0]?.page || 1);
      } catch (e) {
        toast.error(errMsg(e));
      }
    })();
  }, [findingId, auditId]);

  useEffect(() => {
    if (!audit) return;
    api.get(`/documents/${audit.document_id}/pages/${pageNum}`)
      .then((r) => setPage(r.data))
      .catch(() => setPage(null));
  }, [audit, pageNum]);

  const activeQuote = useMemo(() => {
    const ev = finding?.evidence?.find((e) => e.page === pageNum);
    return ev?.quote || null;
  }, [finding, pageNum]);

  const review = async (action) => {
    setReviewing(true);
    try {
      const { data } = await api.post(`/results/${findingId}/review`, { action, note: "" });
      setFinding(data);
      toast.success(action === "confirm" ? "Zjištění potvrzeno" : "Zjištění rozporováno — zaznamenáno k lidskému přezkumu");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReviewing(false);
    }
  };

  if (!finding || !audit) {
    return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div></Layout>;
  }

  return (
    <Layout>
      <Button variant="ghost" size="sm" onClick={() => navigate(`/audits/${auditId}`)} data-testid="back-to-results-button"
        className="mb-4 text-slate-600 hover:text-slate-900">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Zpět na výsledky
      </Button>

      <div className="grid gap-6 lg:grid-cols-[45fr_55fr]">
        <div className="fade-in rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="finding-detail-pane">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge finding={finding} />
            <StatusBadge status={finding.status} />
            {finding.requirement_class === "MANDATORY" && (
              <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Povinné</span>
            )}
          </div>
          <h1 className="mt-3 text-xl font-extrabold tracking-tight text-slate-900">{finding.finding_title}</h1>
          <p className="mt-1 font-mono text-xs text-slate-400">{finding.criterion_id} · {finding.area}</p>

          <div className="mt-5 space-y-4 text-sm">
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Kontrolní kritérium</h3>
              <p className="mt-1 text-slate-700">{finding.question}</p>
            </section>
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Co bylo zjištěno</h3>
              <p className="mt-1 text-slate-700" data-testid="finding-summary">{finding.summary}</p>
            </section>
            {finding.reason && (
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Zdůvodnění vyhodnocení</h3>
                <p className="mt-1 text-slate-700">{finding.reason}</p>
              </section>
            )}
            {finding.missing_or_problematic_elements?.length > 0 && (
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Nedoložené / problematické prvky</h3>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-slate-700">
                  {finding.missing_or_problematic_elements.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </section>
            )}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Spolehlivost vyhodnocení</h3>
              {(() => { const cl = confidenceLabel(finding.confidence); return (
                <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="confidence-label">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cl.cls}`}>{cl.label}</span>
                  <span className="text-xs text-slate-500">{cl.note}</span>
                </div>
              ); })()}
            </section>
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Vazba na RVP PV</h3>
              <p className="mt-1 text-slate-700">{(finding.framework_refs || []).join("; ") || finding.source_reference}</p>
            </section>
            {finding.evidence?.length > 0 && (
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Důkazy v dokumentu</h3>
                <div className="mt-1.5 space-y-2">
                  {finding.evidence.map((ev, i) => (
                    <button key={i} data-testid={`evidence-link-${i}`} onClick={() => setPageNum(ev.page)}
                      className={`block w-full rounded-lg border p-3 text-left text-xs transition-colors ${pageNum === ev.page ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <span className="font-semibold text-emerald-700">Str. {ev.page}</span>
                      {ev.section && <span className="text-slate-500"> · {ev.section}</span>}
                      <p className="mt-1 italic text-slate-600">„{ev.quote?.slice(0, 180)}…“</p>
                    </button>
                  ))}
                </div>
              </section>
            )}
            {finding.verifier && (
              <section className="rounded-lg bg-slate-50 p-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Verifikace (druhá kontrola)</h3>
                <p className="mt-1 text-xs text-slate-600">{finding.verifier.note}</p>
              </section>
            )}
          </div>

          {!audit?.demo && (
          <div className="mt-6 border-t border-slate-100 pt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Lidské ověření</h3>
            {finding.human_review ? (
              <p className="mt-1.5 text-sm text-slate-600" data-testid="human-review-status">
                {finding.human_review.action === "confirm" ? "✓ Potvrzeno" : "✗ Rozporováno"} uživatelem {finding.human_review.user_email}
              </p>
            ) : (
              <div className="mt-2 flex gap-2">
                <Button size="sm" disabled={reviewing} onClick={() => review("confirm")} data-testid="finding-confirm-button"
                  className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Potvrdit zjištění
                </Button>
                <Button size="sm" variant="outline" disabled={reviewing} onClick={() => review("dispute")} data-testid="finding-dispute-button"
                  className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800">
                  <XCircle className="mr-1.5 h-3.5 w-3.5" /> Rozporovat
                </Button>
              </div>
            )}
          </div>
          )}
        </div>

        <div className="fade-in flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="document-viewer-pane">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <BookOpen className="h-4 w-4 text-emerald-600" />
              Vaše ŠVP — strana {pageNum}{page?.page_count ? ` / ${page.page_count}` : ""}
            </div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" data-testid="doc-viewer-prev-page" disabled={pageNum <= 1}
                onClick={() => setPageNum((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" data-testid="doc-viewer-next-page" disabled={page && pageNum >= page.page_count}
                onClick={() => setPageNum((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-6">
            {page ? (
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}
                data-testid="doc-page-text">
                {highlight(page.text, activeQuote)}
              </pre>
            ) : (
              <p className="text-sm text-slate-400">Stranu se nepodařilo načíst.</p>
            )}
          </div>
          {activeQuote && (
            <div className="border-t border-slate-100 px-5 py-2 text-xs text-slate-400">
              Žlutě zvýrazněná pasáž je citace, ze které AI při vyhodnocení vycházela.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
